import React, { useCallback, useState, useEffect } from "react"
import { FaceRecognitionBatch } from "../FaceRecognitionBatch"
import { LoginBatch } from "../LoginBatch"
import { PersonalDocumentBatch } from "../PersonalDocumentBatch"
import {DeviceScan} from "../DeviceScan"
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { NotFoundPage } from '../NotFoundPage'
import { ExpiredPage } from '../ExpiredPage'
import { LoadingPage } from '../LoadingPage'
import { CanceledPage } from '../CanceledPage'
import { defaultThemeSettings, cpfMask, formatToYYYYMMDD } from '../../utils/utils'
import { SignedPageBatch } from "../SignedPageBatch"
import { OTPPageBatch } from "../OTPPageBatch"
import ErrorBoundaryComponent from "../ErrorBoundary"
import { useCookies } from 'react-cookie'
import { v4 as uuidv4 } from 'uuid'
import { setDatadogPageAction } from '../../services/datadog'

export const SignerBatchMainContainer = (props) => {

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [expired, setExpired] = useState(false)
    const [canceled, setCanceled] = useState(false)
    const [clientThemeSettings, setClientThemeSettings] = useState(undefined)
    const { signerBatchHash } = useParams()
    const [signerBatchStep, setSignerBatchStep] = useState("")
    const [signBatchData, setSignBatchData] = useState({})
    const [latitude, setLatitude] = useState(undefined)
    const [longitude, setLongitude] = useState(undefined)
    const [cookies, setCookie] = useCookies([`qisign_${signerBatchHash}`])
    const [alreadySigned, setAlreadySigned] = useState(false)

    useEffect(() => {
        setDatadogPageAction(`/sb/${signerBatchHash} [@step=${signerBatchStep}]`, {
            signerBatchHash,
            signerBatchStep,
            signBatchData
        })
    }, [signerBatchHash, signerBatchStep, signBatchData])

    const collectGeoLocation = useCallback(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLatitude(position.coords.latitude)
                    setLongitude(position.coords.longitude)
                }
            )
        }
    }, [])

    const onSendOTPCode = (signer_token) => {
        const data = {
            "event_date": new Date().toISOString()
        }
        const config = {
            headers:
            {
                'Authorization': signer_token
            }
        }
        axios.post('/signer_batch/authentication', data, config)
            .then((response) => {
            })
            .catch((error) => {
            })
    }

    const submitLogin = useCallback((documentNumber, birthdate) => {
        const payload = {
            "document_number": cpfMask(documentNumber)
        }

        if (birthdate) {
            payload["birthdate"] = formatToYYYYMMDD(birthdate)
        }

        if (latitude !== undefined & longitude !== undefined) {
            payload["gps_data"] = {
                "coordinates": [
                    longitude,
                    latitude
                ]
            }
        }

        setLoading(true)
        axios.post('/signer_batch/hash/' + signerBatchHash, payload)
            .then(async (response) => {
                const session_id = uuidv4()
                setSignBatchData({ ...signBatchData, ...response.data, session_id })
                setCookie(`qisign_${signerBatchHash}`, payload, { path: `/sb/${signerBatchHash}`, maxAge: 60 * 60 })
                setSignerBatchStep(response.data.next_step)
            }).catch((error) => {
                setCookie(`qisign_${signerBatchHash}`, cookies, { path: `/sb/${signerBatchHash}`, maxAge: 0 })
            })
            .finally(() => {
                setLoading(false)
            })
    }, [setCookie, setSignBatchData, latitude, longitude, signerBatchHash, signBatchData, setSignerBatchStep, cookies])

    useEffect(() => {
        collectGeoLocation()

        if (clientThemeSettings) return
        const defaultClientThemeSettings = {
            "logo_url": "https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_azul_claro_escuro_principal.png",
            "font_color": "#000000",
            "button_color": "#4FCCED",
            "email_logo_url": "https://qisign-app-production.s3.amazonaws.com/logos/qi_tech_branco_email.png",
            "background_color": "#0E3573"
        }

        axios.get('/signer_batch/hash/' + signerBatchHash)
            .then((response) => {
                setSignBatchData(response.data)
                setClientThemeSettings(response.data.client_theme_settings ? response.data.client_theme_settings : defaultClientThemeSettings)
                if (["signed", "sending_sign_receipt", "receipt_sent", "analyzing", "analyzed", "completed" ].includes(response.data.status)) {
                    if (response.data.status !== "signed") {
                        setAlreadySigned(true)
                    }
                    setSignerBatchStep("signed")
                } else if (response.data.status === "canceled") {
                    setCanceled(true)
                } else if (response.data.status === "expired") {
                    setExpired(true)
                }
                else {
                    setSignerBatchStep("login")
                }
            }).catch((error) => {
                setError(true)
            })
            .finally(() => {
                const documentNumberParamQueryParam = new URLSearchParams(window.location.search).get('document_number')
                const birthdateParamQueryParam = new URLSearchParams(window.location.search).get('birthdate')

                if (documentNumberParamQueryParam) {
                    submitLogin(documentNumberParamQueryParam, birthdateParamQueryParam)
                }
                setLoading(false)
            })
    }, [signerBatchHash, clientThemeSettings, submitLogin, collectGeoLocation])

    const getScenes = () => {
        if (loading) {
            return (
                <LoadingPage clientThemeSettings={clientThemeSettings ? clientThemeSettings : defaultThemeSettings} />
            )
        }

        if (error) {
            return (
                <NotFoundPage clientThemeSettings={clientThemeSettings ? clientThemeSettings : defaultThemeSettings} />
            )
        }

        if (canceled) {
            return (
                <CanceledPage clientThemeSettings={clientThemeSettings ? clientThemeSettings : defaultThemeSettings} />
            )
        }

        if (expired) {
            return (
                <ExpiredPage clientThemeSettings={clientThemeSettings ? clientThemeSettings : defaultThemeSettings} />
            )
        }

        if (signerBatchStep === "login") {
            return (
                <LoginBatch
                    setSignerBatchStep={setSignerBatchStep}
                    clientThemeSettings={clientThemeSettings}
                    signBatchData={signBatchData}
                    setSignBatchData={setSignBatchData}
                    onSendOTPCode={onSendOTPCode}
                />
            )
        }
        else if (signerBatchStep === "face_recognition") {
            return (
                <FaceRecognitionBatch
                    setSignerBatchStep={setSignerBatchStep}
                    clientThemeSettings={clientThemeSettings}
                    signBatchData={signBatchData}
                    setSignBatchData={setSignBatchData}
                    signerBatchHash={signerBatchHash}
                />
            )
        } else if (signerBatchStep === "personal_document") {
            return (
                <PersonalDocumentBatch
                    setSignerBatchStep={setSignerBatchStep}
                    signBatchData={signBatchData}
                    clientThemeSettings={clientThemeSettings}
                    setSignBatchData={setSignBatchData}
                />
            )
        }
        else if (signerBatchStep === "device_scan") {
            return (
                <DeviceScan
                    setSignerStep={setSignerBatchStep}
                    clientThemeSettings={clientThemeSettings}
                    signData={signBatchData}
                    setSignData={setSignBatchData}
                    isBatch={true}
                />
            )
        }
        else if (signerBatchStep === "authentication_code") {
            return (
                <OTPPageBatch
                    setSignerBatchStep={setSignerBatchStep}
                    clientThemeSettings={clientThemeSettings}
                    setSignBatchData={setSignBatchData}
                    signBatchData={signBatchData}
                    onSendOTPCode={onSendOTPCode}
                />
            )
        } else if (signerBatchStep === "signed") {
            return (
                <SignedPageBatch
                    clientThemeSettings={clientThemeSettings}
                    alreadySigned={alreadySigned}
                    signerBatchHash={signerBatchHash}
                />
            )
        } else {
            return (
                <NotFoundPage clientThemeSettings={clientThemeSettings ? clientThemeSettings : defaultThemeSettings} />
            )
        }
    }

    return (
        <ErrorBoundaryComponent clientThemeSettings={clientThemeSettings}>
            {getScenes()}
        </ErrorBoundaryComponent>
    )
}